import React, { useEffect, useState, Fragment, useMemo } from 'react';
import { connect } from 'react-redux';
import {
  Col, Row, CardBody, Card, Collapse, FormGroup, Alert,
  CardText,
} from 'reactstrap';
import LaddaButton, { ZOOM_OUT } from 'react-ladda';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import TextInput from '../../components/Inputs/TextInput';
import ContactCard from '../../components/Widgets/ContactCard';
import {
  assignActivityIsOpenFields,
  getTicket,
  toggleActivity,
  ticketToggleModal,
  toggleTicketReasonDropdownOpen,
  toggleTicketModalLoading,
  toggleTicketEscalationModalLoading,
  toggleTicketLoading,
  setTicketCommentFormOpen,
  setTicketCommentValue,
  setDescError,
  setCommentProcessing,
  ticketUpdateEscalationNote,
  ticketEscalationToggleModal,
  ticketEscalationSetErrorText,
  updateTicketEscalated,
  getTicketActivity, toggleActivityContentLoading, deletePortalUserComment, setActivityError,
} from '../../store/ticket/actions';
import { updateTicket } from '../../utils/Tickets/Tickets';
import { IActivity, ITicketState } from '../../store/ticket/types';

import Section from '../../components/Section';
import { Gravatar } from '../../components/Gravatar/Gravatar';
import TextEditor from '../../components/TextEditor/TextEditor';
import EditIconButton from '../../components/Buttons/EditIconButton';
import SimpleLoader from '../../components/SimpleLoader';
import EscalateTicket from '../../components/Modals/EscalateTicket';
import { IUserState } from '../../store/user/types';
import { checkPermission } from '../../utils/Auth/AuthService';
import { successFeedback } from '../../actions/feedback';
import { IAccountState } from '../../store/account/types';
import { AppDispatch } from '../../configureStore';
import DeleteIconButton from "../../components/Buttons/DeleteIconButton";
import { resetSystemConfirmation, setSystemConfirmation } from "../../store/system/actions";
import { deleteContact, setContactErrorMessage, toggleContactLoading } from "../../store/contact/actions";
import ErrorCallout from "../../components/ErrorCallout";
import ErrorGeneric from "../../components/ErrorGeneric";
import ticket from '../../views/Tickets/Ticket'

type TParams = { id: string };
type TicketProps = {
  user: IUserState;
  dispatch: AppDispatch;
  theTicket: ITicketState;
  account: IAccountState;
};
type Props = TicketProps;

enum DescriptionFields {
  Type = 'Type',
  Title = 'Title',
  Category = 'Category',
  Priority = 'Priority',
  Device = 'Device',
  Site = 'Site',
  Description = 'Description'
}

interface ParsedField {
  fieldName: DescriptionFields;
  value: string;
}

const getActivityType = (type, email) => {

  switch (type) {
    case 'email':
    case 'Email':
      if (email.match(/(syntura.io|hso.uk.com|goscomb.net)/)) {

        return { cssClass: 'accord--email-inbound', label: 'Inbound email' };

      }
      return { cssClass: 'accord--email-outbound', label: 'Outbound email' };
    case 'escalation':
      return { cssClass: 'accord--escalation', label: 'Escalation' };
    case 'portalUserComment':
      return { cssClass: 'accord--portal-user-comment', label: 'Comment' };
    case 'Phone Call':
      return { cssClass: 'accord--phone', label: 'Phone' };
    case 'Case Resolution':
      return { cssClass: 'accord--resolved', label: 'Resolved' };
    default:
      return { cssClass: '', label: '' };
  }

};

const cleanHtmlString = (html) => {
  if (!html) return '';

  // First, decode escaped characters
  let cleaned = html
    .replace(/\\\//g, '/')           // Fix escaped forward slashes
    .replace(/\\"/g, '"')            // Fix escaped quotes
    .replace(/\\n/g, '\n')           // Fix escaped newlines
    .replace(/\\t/g, '\t')           // Fix escaped tabs
    .replace(/\\u2019/g, "'")        // Fix escaped apostrophe
    .replace(/\\u2018/g, "'")        // Fix escaped left single quote
    .replace(/\\u201c/g, '"')        // Fix escaped left double quote
    .replace(/\\u201d/g, '"')        // Fix escaped right double quote
    .replace(/\\u2022/g, '•')        // Fix escaped bullet
    .replace(/\\u2013/g, '–')        // Fix escaped en dash
    .replace(/\\u2014/g, '—');       // Fix escaped em dash

  const tableMatch = cleaned.match(/<table><tbody>([\s\S]*?)<\/tbody><\/table>/);
  if (tableMatch) {
    const tableContent = tableMatch[1];

    const rows = tableContent.split('</tr>').filter(row => row.trim());

    let newTableHtml = '<table class="table table-bordered table-striped">';

    rows.forEach((row, index) => {
      if (row.trim()) {
        // Extract all td elements from this row
        const tdMatches = row.match(/<td[^>]*>([^<]*)<\/td>/g);

        if (tdMatches && tdMatches.length > 0) {
          newTableHtml += '<tr>';

          tdMatches.forEach((td, tdIndex) => {
            const content = td.replace(/<td[^>]*>([^<]*)<\/td>/, '$1');

            if (index === 0) {
              // First row becomes header
              newTableHtml += `<th>${content}</th>`;
            } else {
              // Other rows become data cells
              newTableHtml += `<td>${content}</td>`;
            }
          });

          newTableHtml += '</tr>';
        }
      }
    });

    newTableHtml += '</table>';

    // Replace the original table with the new one
    cleaned = cleaned.replace(/<table><tbody>[\s\S]*?<\/tbody><\/table>/, newTableHtml);
  }

  // Use DOM parser to properly decode HTML entities
  const txt = document.createElement('textarea');
  txt.innerHTML = cleaned;
  cleaned = txt.value;

  return cleaned.trim();
}

const Ticket: React.FC<Props> = (props) => {

  const { dispatch, account } = props;
  const {
    data,
    loading,
    showCommentForm,
    commentValue,
    commentProcessing,
    descError,
    reasonDropdownOpen,
    ticketModalLoading,
    escalationticketModalLoading,
  } = props.theTicket;
  const {
    id,
    ticketNumber,
    title,
    description,
    status,
    statusReason,
    origin,
    priority,
    assignedTo,
    openedBy,
    createdOn,
    modifiedOn,
    activities,
    escalationNote,
    escalationModalOpen,
    modalOpen,
    escalateModalErrorText,
  } = data;
  const params = useParams();
  const [isLegacyTicket, setIsLegacyTicket] = useState<boolean>(false);

  const parseStructuredDescription = (descriptionString: string): ParsedField[] => {
    if (!descriptionString || typeof descriptionString !== 'string') {
      return [];
    }

    const result: Record<string, string> = {};
    // Group 1: A capitalized word ([A-Z][a-z0-9]*)
    // Optional Non-capturing Group: Optionally followed by <p>...</p>
    // Group 2: The content inside the <p> tags (if they exist)
    const regex = /([A-Z][a-z0-9]*)(?:<p>(.*?)<\/p>)?/g;

    let match;
    while ((match = regex.exec(descriptionString)) !== null) {
      const fieldName = match[1]; // The captured capitalized word
      const value = match[2] ?? "--"; // The field value or "--" if empty
      result[fieldName] = value;
    }

    return Object.entries(result).map(([fieldName, value]) => ({
      fieldName: fieldName as DescriptionFields,
      value
    }));
  };

  const getC = (id) => {

    dispatch(toggleTicketLoading());
    dispatch(getTicket(id, { include: 'activities' })).then(() => {

      dispatch(toggleTicketLoading());
      dispatch(assignActivityIsOpenFields());

    });

  };


  useEffect(() => {
    getC(params.id);
  }, [params.id]);

  useEffect(() => {
    checkIfLegacyTicket(ticketNumber);
  }, [ticketNumber]);


  const checkIfLegacyTicket = (ticketId) => {
    if (ticketId.startsWith('CAS')) {
      setIsLegacyTicket(true);
    } else {
      setIsLegacyTicket(false);
    }
  };

  const escalateC = () => {

    if (!escalationNote) {

      dispatch(ticketEscalationSetErrorText('Please describe why the ticket is being escalated'));

    } else if (escalationNote.length > 250) {

      dispatch(ticketEscalationSetErrorText(`Maximum length is 250 characters you have ${escalationNote.length} characters.`));

    } else {

      dispatch(toggleTicketEscalationModalLoading());
      dispatch(updateTicketEscalated(id, escalationNote)).then((escalated) => {

        if (escalated === true) {

          dispatch(ticketEscalationToggleModal());
          dispatch(successFeedback(`${ticketNumber} has been escalated!`));
          getC(params.id);

        }
        dispatch(toggleTicketEscalationModalLoading());

      });

    }

  };

  const ticketResolved = useMemo(() => status === 'Resolved' || status === 'Canceled', [status]);
  const toggleComment = () => {

    dispatch(setTicketCommentFormOpen());

  };

  const toggleActivityState = (activityId) => {

    const activity = activities.find((act) => activityId === act.id);
    if (activity && !activity.isOpen && !activity.content && activity.type !== 'Ticket Resolution') {

      dispatch(toggleActivityContentLoading(activityId));
      dispatch(getTicketActivity(id, activityId)).then(() => dispatch(toggleActivityContentLoading(activityId)));

    }
    dispatch(toggleActivity(activityId));

  };
  const datacallback = (fromChild) => {

    dispatch(setTicketCommentValue(fromChild));

  };
  const updateC = () => {

    if (commentValue === '' || commentValue === null || commentValue.replace(/&nbsp;/g, '').replace(/<p[^>]*>/g, '').replace(/<\/p>/g, '').trim() === '') {

      dispatch(setDescError(true));
      return false;

    }
    dispatch(setCommentProcessing(true));
    updateTicket(params.id, commentValue).then((result) => {

      toggleComment();
      getC(params.id);
      dispatch(setCommentProcessing(false));

    });

  };
  const createMarkup = (markup) => ({ __html: markup });

  const onEscalateChange = (event) => {

    dispatch(ticketUpdateEscalationNote(event.target.value));

  };

  const toggleModal = () => {

    dispatch(ticketToggleModal());

  };
  const ticketEscalationModal = () => {

    dispatch(ticketEscalationToggleModal());

  };
  const toggleTicketReasonDropdownOpenHandler = () => {

    dispatch(toggleTicketReasonDropdownOpen());

  };

  const onCommentDelete = (id: string) => {

    dispatch(setSystemConfirmation(
      {
        text: 'This will remove the comment from this ticket and you will not be able to see it again.',
        isOpen: true,
        color: 'danger',
        proceed: () => {
          dispatch(toggleActivityContentLoading(id));
          dispatch(deletePortalUserComment(id)).then((result) => {
            if (!result) {
              dispatch(setActivityError(id, 'Could not delete comment.'));
            } else {
              getC(params.id);
            }
            dispatch(toggleActivityContentLoading(id));
          });
          dispatch(resetSystemConfirmation());
        },
        cancel: () => {
          dispatch(resetSystemConfirmation());
        },
      },
    ));

  }

  const formatDateTime = (date) => {
    return moment(date).format('DD/MM/YYYY HH:mm:ss');
  };

  return (
    <div className="animated fadeIn mb-5" style={{ minHeight: '500px', fontSize: '16px' }}>
      <SimpleLoader loading={loading} text="Loading your ticket...">
        {!loading && (
          <div>

            <div className="animated fadeIn mb-3">
              <Section title={`${ticketNumber} - ${title ? `${title.substring(0, 100)}` : ''}`}>
                {isLegacyTicket ? (
                  <Alert color="warning">
                    This is a legacy ticket. For any inquiries,
                    <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>
                      please contact support.
                    </a>
                  </Alert>
                ) : null}
                <Row className="bg-white pt-4 pl-4 m-0" style={{ borderRadius: '10px' }}>
                  <Col sm="4">
                    <TextInput id="Title" label="Title" value={title} truncate readOnly />
                    <TextInput
                      id="Number"
                      label="Ticket Number"
                      value={ticketNumber}
                      readOnly
                    />
                    <TextInput
                      id="Created"
                      label="Created"
                      value={moment(createdOn).format('DD/MM/YYYY')}
                      readOnly
                    />
                    <TextInput
                      id="Modified"
                      label="Modified"
                      value={moment(modifiedOn).format('DD/MM/YYYY')}
                      readOnly
                    />
                    <TextInput
                      id="Origin"
                      label="Origin"
                      truncate
                      value={origin}
                      readOnly
                    />
                  </Col>
                  <Col sm="4">
                    <TextInput
                      id="Priority"
                      label="Priority"
                      truncate
                      value={priority}
                      readOnly
                    />
                    <TextInput id="Status" label="Status" truncate value={status} readOnly />
                    <TextInput id="StatusReason" label="Status Reason" truncate value={statusReason} readOnly />
                    <TextInput
                      id="Opened By"
                      label="Opened By"
                      truncate
                      value={openedBy}
                      readOnly
                    />
                  </Col>
                  <Col sm="4">
                    <h5><strong>Your Ticket Manager</strong></h5>
                    <ContactCard
                      dropShadow={false}
                      firstName={assignedTo || 'NOC'}
                      lastName=""
                      email="support@syntura.io"
                      phone={account.hsoSupport}
                      jobTitle="Ticket Manager"
                    />
                  </Col>
                </Row>
                <Row className="bg-white p-4 m-0 mt-2" style={{ borderRadius: '10px' }}>
                  <Col>
                    {description && description.includes('<p>') ? (
                      <div className="structured-description"dangerouslySetInnerHTML={{ __html: cleanHtmlString(description) }} />
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: description}} />
                    )}
                  </Col>
                </Row>
              </Section>
            </div>
              <Section title="Activity">
                {!ticketResolved && checkPermission('cases.write', props.user.permissions) && (
                  <Row>
                    <Col>
                      <LaddaButton
                        className="btn btn-primary btn-ladda px-4 mb-4"
                        data-color="blue"
                        data-style={ZOOM_OUT}
                        onClick={ticketEscalationModal}
                        disabled={isLegacyTicket}
                      >
                        Escalate
                      </LaddaButton>
                    </Col>
                  </Row>
                )}
                <div className="card p-5 accord" style={{ borderRadius: '10px' }}>
                  <div className="row d-flex justify-content-end mb-4">
                    <div className=" d-flex justify-content-end ">
                      {!ticketResolved && checkPermission('cases.write', props.user.permissions) && (
                        <EditIconButton onClick={toggleComment} disabled={isLegacyTicket} />
                      )}
                    </div>
                  </div>
                  <Collapse isOpen={showCommentForm}>
                    <Row>
                      <Col>
                        <FormGroup color="null">
                          <TextEditor callback={datacallback} color={'descError ? "danger" : null'} />
                          {descError && (
                            <p className="mt-1 text-danger">Comment text is a required field</p>
                          )}
                          <div
                            className="clearfix"
                            style={{ marginTop: `${10}px`, marginBottom: `${20}px` }}
                          >
                            <LaddaButton
                              style={{ minWidth: `${113}px`, minHeight: `${35}px` }}
                              className="btn btn-primary btn-ladda px-4 float-right"
                              disabled={commentProcessing}
                              loading={commentProcessing}
                              data-color="blue"
                              data-style={ZOOM_OUT}
                              onClick={() => updateC()}
                            >
                              Add Comment
                            </LaddaButton>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Collapse>
                  {activities && activities.map((activity) => (
                    <Fragment key={activity.id}>
                      <div
                        className={` accord__bar p-3 mb-2 ${getActivityType(activity.type, activity.emailAddresses.from).cssClass}`}
                      >
                        <Row
                          className="mb-2"
                          onClick={() => toggleActivityState(activity.id)}
                        >
                          <Col className="d-flex align-items-center" md="2">
                            <span
                              className="text-upperticket"
                            >
                              {' '}
                              {getActivityType(activity.type, activity.emailAddresses.from).label}
                              {' '}

                            </span>
                          </Col>
                          <Col className="d-flex align-items-center text-dark" md="2">
                            <span>
                              {formatDateTime(activity.modifiedOn)}
                            </span>
                          </Col>
                          <Col className="d-flex align-items-center text-dark" md="2">
                            <span>{activity.createdBy}</span>
                          </Col>
                          <Col className="d-flex align-items-center text-dark" md="4">
                            <span className="text-wrap">{activity.subject}</span>
                          </Col>
                          <Col className="d-flex align-items-center text-light" md="2">
                            {activity.isOpen && (<i className="fa fa-minus text-light ml-auto" />)}
                            {!activity.isOpen && (<i className="fa fa-plus text-light  ml-auto" />)}
                          </Col>
                        </Row>
                        <Row>
                          <Col md="12">
                            <Collapse isOpen={activity.isOpen}>
                              <Card style={{ boxShadow: 'none', border: 'none' }}>
                                <CardBody>
                                  <SimpleLoader loading={activity.contentLoading}>
                                    <CardText style={{ whiteSpace: 'pre-line', marginBottom: '4px' }}>
                                      <strong>From:</strong> {activity.emailAddresses && activity.emailAddresses.from ? activity.emailAddresses.from : (activity.createdBy ? activity.createdBy : '')}
                                    </CardText>
                                    <CardText style={{ whiteSpace: 'pre-line', marginBottom: '4px' }}>
                                      <strong>Sent:</strong> {formatDateTime(activity.createdOn)}
                                    </CardText>
                                    <CardText style={{ whiteSpace: 'pre-line', marginBottom: '4px' }}>
                                      <strong>To:</strong> {activity.emailAddresses?.to || ''}
                                    </CardText>
                                    {activity.emailAddresses.cc && (
                                      <CardText style={{ whiteSpace: 'pre-line', marginBottom: '4px' }}>
                                        <strong>Cc:</strong> {activity.emailAddresses.cc}
                                      </CardText>
                                    )}
                                    <CardText style={{ whiteSpace: 'pre-line', marginBottom: '4px' }}>
                                      <strong>Subject:</strong> <span style={{ fontWeight: 600 }}>{activity.subject}</span>
                                    </CardText>
                                    {activity.type === 'portalUserComment' && (
                                      <DeleteIconButton onClick={() => onCommentDelete(activity.id)} disabled={isLegacyTicket}/>
                                    )}

                                    {activity.error && (
                                      <Alert color={'danger'} className={'mt-4'}>activity.error'</Alert>
                                    )}
                                    {activity.content && (
                                      <Card className="mt-4" style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'auto', width: '100%' }}>
                                        <CardBody style={{ minHeight: 100 }}>
                                          <div dangerouslySetInnerHTML={{ __html: activity.content }} />
                                        </CardBody>
                                      </Card>
                                    )}
                                  </SimpleLoader>
                                </CardBody>
                              </Card>
                            </Collapse>
                          </Col>
                        </Row>
                      </div>
                    </Fragment>
                  ))}
                </div>
              </Section>
          </div>
        )}
      </SimpleLoader>
      <EscalateTicket
        isOpen={escalationModalOpen}
        cancel={() => dispatch(ticketEscalationToggleModal())}
        escalationNote={escalationNote}
        changeHandler={(event) => onEscalateChange(event)}
        error={escalateModalErrorText}
        escalationticketModalLoading={escalationticketModalLoading}
        proceed={escalateC}
      />
    </div>
  );

};
function mapStateToProps({ user, ticket: theTicket, account }) {

  return {
    user, theTicket, account,
  };

}
export default connect(mapStateToProps)(Ticket);

